import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LgmButton from '../../LgmButton';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import { useManualUnlinkManufacturer } from '../../../services/api/domains/manufacturers/hooks';
import { GRAY_200, GRAY_900 } from 'theme/grayScale';
import { COLOR_DANGER_TEXT } from 'theme/colors';

export interface BreakLinkConfirmDialogProps {
  manufacturerId: number;
  source: string;
  sourceLabel: string;
  naturalKey: string;
  displayName: string;
  onClose: () => void;
  onUnlinked: () => void;
}

/**
 * Shared unlink confirmation dialog for manufacturer source links
 * (MFGV2-003, Sub-task 3.2) — pairs with LinkToSourceModal. Writes a
 * Manufacturer_Link_Rejections tombstone (api/sql/manufacturerLinkUnlink.js)
 * so a subsequent automatic matching pass never re-links the same pair.
 *
 * `zIndex: 20000` — see LinkToSourceModal.tsx's identical comment; without
 * it this dialog renders behind the app's custom modal overlay (z-index
 * 10000), invisible but still present in the DOM.
 */
const BreakLinkConfirmDialog: React.FC<BreakLinkConfirmDialogProps> = ({
  manufacturerId,
  source,
  sourceLabel,
  naturalKey,
  displayName,
  onClose,
  onUnlinked,
}) => {
  const unlinkMutation = useManualUnlinkManufacturer();
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isUnlinking = unlinkMutation.status === 'loading';

  useModalBackDismiss({
    isOpen: true,
    onClose,
    canDismiss: () => !isUnlinking,
  });

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await unlinkMutation.mutate({
        manufacturerId,
        source,
        naturalKey,
        reason: reason.trim() || undefined,
      });
      onUnlinked();
    } catch {
      setErrorMessage('Failed to remove the link. Try again.');
    }
  };

  return (
    <Dialog
      open
      onClose={isUnlinking ? undefined : () => onClose()}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 20000 }}>
      <DialogTitle>Unlink from {sourceLabel}</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          style={{ color: GRAY_900, marginBottom: '10px' }}>
          Remove this manufacturer&apos;s link to <strong>{displayName}</strong>
          ? This won&apos;t be re-linked automatically — a future matching pass
          will skip it.
        </Typography>
        <textarea
          placeholder="Reason (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={2}
          style={{
            width: '100%',
            padding: '8px 10px',
            fontSize: '0.8125rem',
            border: `1px solid ${GRAY_200}`,
            borderRadius: '4px',
            boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
        {errorMessage && (
          <Typography
            variant="body2"
            style={{ color: COLOR_DANGER_TEXT, marginTop: '8px' }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LgmButton variant="flat" onClick={onClose} disabled={isUnlinking}>
          Cancel
        </LgmButton>
        <LgmButton
          variant="contained"
          color="danger"
          onClick={handleConfirm}
          disabled={isUnlinking}>
          {isUnlinking ? 'Removing...' : 'Remove Link'}
        </LgmButton>
      </DialogActions>
    </Dialog>
  );
};

export default BreakLinkConfirmDialog;
